import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";

export type CompanyStatus = "active" | "frozen" | "stopped";
export type EntryKind = "charge" | "pay" | "received";
export interface DeliveryEntry {
  id: string; k: EntryKind; amt: string; br: string; d: string; no: string;
  why: string; by: string; settle: boolean;
}
export interface StatusHistory {
  id: string; s: CompanyStatus; action: "added" | "status"; by: string;
  role: string; d: string; why: string; until: string | null;
}
export interface CompanyFields {
  en: string; ar: string; br: "all" | string[]; person: string; phone: string;
}
export interface DeliveryCompany extends CompanyFields {
  id: string; status: CompanyStatus; hist: StatusHistory[]; entries: DeliveryEntry[];
}
export interface DeliveryBranch { id: string; name: string; nameAr: string | null }
export interface DeliveryResponse {
  companies: DeliveryCompany[]; branches: DeliveryBranch[];
  capabilities: { manage: boolean; status: boolean; record: boolean; allbr: boolean };
  actor: { name: string; role: string }; today: string;
}
export const deliveryKey = (workspaceId: string) => ["/api/delivery-companies", workspaceId] as const;
const endpoint = (workspaceId: string, suffix = "") =>
  `/api/delivery-companies${suffix}?workspaceId=${encodeURIComponent(workspaceId)}`;

export function useDeliveryCompanies(workspaceId: string, id?: string) {
  return useQuery<DeliveryResponse>({
    queryKey: [...deliveryKey(workspaceId), id || "list"],
    queryFn: async () => (await apiRequest("GET", endpoint(workspaceId, id ? `/${encodeURIComponent(id)}` : ""))).json(),
    enabled: !!workspaceId,
  });
}
export function useSaveDeliveryCompany(workspaceId: string) {
  return useMutation({
    mutationFn: async ({ id, fields }: { id?: string; fields: CompanyFields }): Promise<{ id: string }> =>
      (await apiRequest(id ? "PATCH" : "POST", endpoint(workspaceId, id ? `/${encodeURIComponent(id)}` : ""), fields)).json(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deliveryKey(workspaceId) }),
  });
}
export interface StatusChange {
  id: string; status: CompanyStatus; reason: string; until: string | null;
  confirmed: boolean; expectedStatus: CompanyStatus;
}
export function useChangeDeliveryStatus(workspaceId: string) {
  return useMutation({
    mutationFn: async ({ id, ...body }: StatusChange): Promise<{ id: string }> =>
      (await apiRequest("POST", endpoint(workspaceId, `/${encodeURIComponent(id)}/status`), body)).json(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deliveryKey(workspaceId) }),
  });
}
